import { Button, FlexBox } from '@dixa/lib-shared-habla';
import { sizes } from '@dixa/lib-shared-tokens';
import classnames from 'classnames';
import { PropsWithClassName } from 'lib-shared-types-utils';
import { useCallback } from 'react';

import { useFeatureFlag } from 'utils/featureFlagging/useFeatureFlag';
import styles from './activityLogFilters.module.scss';
import { ActivityLogFiltersValue } from './activityLogFilters.types';
import { ActivityLogAuthorFilter } from './components/activityLogAuthorFilter';
import { ActivityLogDateRangeFilter } from './components/activityLogDateRangeFilter';
import { EventTypeFilter } from './components/eventTypeFilter';
import { conversationEventTypeLabels } from './utils/eventTypeLabels';

type ActivityLogFiltersProps = PropsWithClassName & {
  /** The current filters. */
  readonly filters: ActivityLogFiltersValue;
  /** Called with the changed subset of filters. */
  readonly onChange: (filters: Partial<ActivityLogFiltersValue>) => void;
};

/** Represents the filters toolbar displayed at the top of the activity log. */
export const ActivityLogFilters = ({
  className,
  filters,
  onChange,
}: ActivityLogFiltersProps): JSX.Element | null => {
  const featureFlags = useFeatureFlag(['activity-log-filters']);

  const handleClearAll = useCallback(() => {
    onChange({
      fromTs: undefined,
      toTs: undefined,
      eventTypes: undefined,
      authorIds: undefined,
    });
  }, [onChange]);

  return featureFlags.activityLogFilters ? (
    <FlexBox
      className={classnames(styles.root, className)}
      flexWrap="wrap"
      alignItems="center"
      justifyContent="flex-end"
      gap={sizes.spacer[200]}
    >
      <ActivityLogDateRangeFilter
        fromTs={filters.fromTs}
        toTs={filters.toTs}
        onChange={onChange}
      />
      <EventTypeFilter
        labels={conversationEventTypeLabels}
        value={filters.eventTypes ?? []}
        onChange={(eventTypes) => onChange({ eventTypes })}
      />
      <ActivityLogAuthorFilter
        value={filters.authorIds ?? []}
        onChange={(authorIds) => onChange({ authorIds })}
      />

      <Button variant="secondary" onClick={handleClearAll}>
        Clear all
      </Button>
    </FlexBox>
  ) : null;
};
